/**
 * Phone Number Formatting Utilities
 * 
 * Provides consistent phone number formatting across the application
 * Format: (111) 111-1111
 */

/**
 * Format phone number as user types
 * @param {string} value - Raw phone number input
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const cleaned = value?.replace(/\D/g, '') || '';
  
  // Limit to 10 digits
  const limited = cleaned?.substring(0, 10);
  
  // Format based on length
  if (limited?.length === 0) {
    return '';
  } else if (limited?.length <= 3) {
    return `(${limited}`;
  } else if (limited?.length <= 6) {
    return `(${limited?.slice(0, 3)}) ${limited?.slice(3)}`;
  } else {
    return `(${limited?.slice(0, 3)}) ${limited?.slice(3, 6)}-${limited?.slice(6)}`;
  }
};

/**
 * Get raw phone number (digits only)
 * @param {string} formatted - Formatted phone number
 * @returns {string} - Raw digits only
 */
export const getRawPhoneNumber = (formatted) => {
  return formatted?.replace(/\D/g, '') || '';
};

/**
 * Validate phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid 10-digit US phone number
 */
export const isValidPhoneNumber = (phone) => {
  const cleaned = getRawPhoneNumber(phone);
  return cleaned?.length === 10;
};

/**
 * Format phone number for display (complete format)
 * @param {string} phone - Raw or formatted phone number
 * @returns {string} - Consistently formatted phone number
 */
export const displayPhoneNumber = (phone) => {
  const cleaned = getRawPhoneNumber(phone);
  
  if (cleaned?.length !== 10) {
    return phone; // Return as-is if not 10 digits
  }
  
  return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3, 6)}-${cleaned?.slice(6, 10)}`;
};